import { api } from '../../services/api';
import { IData, IDataNoPagination } from '../../types/shared';
import { IUser } from '../../hooks/useUsers';
import { IIpWhitelist } from '../../hooks/useIpWhitelist';

export const GRUPO_CLIENTE_ID = 7;

// --- logs de acesso (tb_sessao_log) ------------------------------------------------------

export interface ILogAcesso {
  id: number;
  dt_criacao: string;
  metodo: string | null;
  rota: string | null;
  descricao: string | null;
  endereco_ip: string | null;
  usuario_id: number;
  sessao_id: number;
  usuario_nome: string;
  usuario_email: string;
  navegador: string | null;
  sistema_operacional: string | null;
  dispositivo: string | null;
  tipo_acesso: string | null;
}

export interface IFiltroLogs {
  search?: string;
  usuario_id?: number;
  /** A API filtra por trecho da descrição (ilike). */
  descricao?: string;
  /** ISO; o backend ainda não aplica (entra na construção do backend). */
  data_inicio?: string;
  data_fim?: string;
}

export type TipoEvento = 'login' | 'login-cliente' | 'delegado' | 'saida' | 'token' | 'outro';

/** Lê o tipo do evento pela descrição gravada no login/logout. */
export function tipoDoEvento(log: Pick<ILogAcesso, 'descricao'>): TipoEvento {
  const d = log.descricao ?? '';
  if (d.includes('Acesso Delegado')) return 'delegado';
  if (d.startsWith('Login Cliente')) return 'login-cliente';
  if (d.includes('Login')) return 'login';
  if (d.includes('Logout')) return 'saida';
  if (d.includes('Atualizou Token')) return 'token';
  return 'outro';
}

/**
 * No acesso delegado a descrição traz o token da solicitação: aqui só sai o nome de quem pediu.
 */
export function solicitanteDoDelegado(log: Pick<ILogAcesso, 'descricao'>): string | null {
  const m = /Admin solicitante: (.*?) \(ID/.exec(log.descricao ?? '');
  return m ? m[1] : null;
}

export const acessoApi = {
  logs: async (filtro: IFiltroLogs, page: number, size = 40) => {
    const res = await api.get<IData<ILogAcesso[]>>('/api/v1/logs-acesso/', {
      params: {
        page,
        size,
        search: filtro.search || undefined,
        usuario_id: filtro.usuario_id || undefined,
        descricao: filtro.descricao || undefined,
        data_inicio: filtro.data_inicio || undefined,
        data_fim: filtro.data_fim || undefined,
      },
    });
    return res.data.data;
  },

  // --- clientes da Área VIP (usuários do grupo 7) ----------------------------------------
  clientes: async (filtro: { texto: string; ativo?: boolean }, page: number, size = 20) => {
    const t = filtro.texto.trim();
    let busca: Record<string, string> = {};
    if (/^\d+$/.test(t)) busca = { id: t };
    else if (t.includes('@')) busca = { email: t };
    else if (t) busca = { nome: t };
    const res = await api.get<IData<IUser[]>>('/api/v1/usuarios/', {
      params: { page, size, grupo_id: GRUPO_CLIENTE_ID, ativo: filtro.ativo, ...busca },
    });
    return res.data.data;
  },
  totalDeClientes: async (ativo?: boolean) => {
    const res = await api.get<IData<IUser[]>>('/api/v1/usuarios/', {
      params: { page: 1, size: 1, grupo_id: GRUPO_CLIENTE_ID, ativo },
    });
    return res.data.data.total ?? 0;
  },
  cliente: async (id: number) => {
    const res = await api.get<
      IDataNoPagination<{
        id: number;
        nome: string;
        email: string;
        cpf: string | null;
        ativo: boolean;
        empresas: {
          id: number;
          razao_social: string;
          nome_fantasia: string;
          cnpj: string;
          validado: boolean;
        }[];
      }>
    >(`/api/v1/secao-cliente/${id}/`);
    return res.data.data;
  },
  /** Mesmo PUT que a tela antiga usava (linha inteira da listagem + `ativo`). */
  alterarAtivoDoCliente: async (cliente: IUser, ativo: boolean) => {
    const res = await api.put<IData<IUser>>(`/api/v1/usuarios/${cliente.id}/`, {
      ...cliente,
      ativo,
    });
    return res.data;
  },

  // --- whitelist de IPs --------------------------------------------------------------------
  ips: async () => {
    const res = await api.get<IDataNoPagination<IIpWhitelist[]>>('/api/v1/ip-whitelist/');
    return res.data.data;
  },
  salvarIp: async (ip: Omit<IIpWhitelist, 'id'> & { id?: number }) => {
    const res = ip.id
      ? await api.put<IDataNoPagination<IIpWhitelist>>(`/api/v1/ip-whitelist/${ip.id}/`, ip)
      : await api.post<IDataNoPagination<IIpWhitelist>>('/api/v1/ip-whitelist/', ip);
    return res.data.data;
  },
  removerIp: async (id: number) => {
    await api.delete(`/api/v1/ip-whitelist/${id}/`);
  },
};

export const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(p => /^[A-Za-zÀ-ÿ]/.test(p))
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join('') || '?';

export const cnpjFormatado = (cnpj?: string | null) => {
  const d = (cnpj ?? '').replace(/\D/g, '');
  return d.length === 14
    ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
    : (cnpj ?? '');
};

const FUSO = 'America/Sao_Paulo';
export const horaDoLog = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: FUSO });
export const diaDoLog = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { timeZone: FUSO });
export const quandoFoi = (iso: string) => {
  const hoje = diaDoLog(new Date().toISOString());
  const ontem = diaDoLog(new Date(Date.now() - 864e5).toISOString());
  const dia = diaDoLog(iso);
  if (dia === hoje) return `hoje, ${horaDoLog(iso)}`;
  if (dia === ontem) return `ontem, ${horaDoLog(iso)}`;
  return `${dia}, ${horaDoLog(iso)}`;
};
export const tituloDoDia = (iso: string) => {
  const dia = diaDoLog(iso);
  const extenso = new Date(iso).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: FUSO,
  });
  if (dia === diaDoLog(new Date().toISOString())) return `Hoje · ${extenso}`;
  if (dia === diaDoLog(new Date(Date.now() - 864e5).toISOString())) return `Ontem · ${extenso}`;
  const ano = dia.slice(-4);
  const comAno =
    ano === diaDoLog(new Date().toISOString()).slice(-4) ? extenso : `${extenso} de ${ano}`;
  return comAno.charAt(0).toUpperCase() + comAno.slice(1);
};
